import React, { useState, useEffect } from "react";
import {
  Layout,
  Menu,
  Button,
  Typography,
  Dropdown,
  Spin,
  Drawer,
  Grid,
} from "antd";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../../../config";
import {
  DashboardOutlined,
  ShopOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ScheduleOutlined,
  TeamOutlined,
  TruckOutlined,
  ContactsOutlined,
  MenuOutlined,
} from "@ant-design/icons";

const { Sider, Content, Header } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const SIDER_WIDTH = 210;
const HEADER_HEIGHT = 48;

const NAV_ITEMS = [
  {
    key: "/AdminDashBoard",
    icon: <DashboardOutlined />,
    label: "Dashboard",
  },
  {
    key: "products",
    icon: <ShopOutlined />,
    label: "Products",
    children: [
      { key: "/AdminAllProducts", label: "All Products" },
      { key: "/AdminCategory", label: "By Category" },
      { key: "/NewLaunch", label: "New Launch" },
      { key: "/ManageProductTags", label: "Product Tags" },
      { key: "/ManageLastChanceDeals", label: "Last Chance Deals" },
      { key: "/ManageTrendingItems", label: "Trending Items" },
    ],
  },
  {
    key: "supplier",
    icon: <TruckOutlined />,
    label: "Supplier",
    children: [
      { key: "/SupplierOrders", label: "Supplier Orders" },
      { key: "/ManageSupplier", label: "Manage Suppliers" },
    ],
  },
  {
    key: "users",
    icon: <TeamOutlined />,
    label: "Users",
    children: [
      { key: "/UserList", label: "User List" },
      { key: "/UserActivitySummary", label: "Activity Summary" },
      { key: "/ProductInsights", label: "Product Insights" },
    ],
  },
  {
    key: "crm",
    icon: <ContactsOutlined />,
    label: "CRM",
    children: [
      { key: "/LeadsToApprove", label: "Leads To Approve" },
      { key: "/DirectRegistrationRequests", label: "Direct Registrations" },
      { key: "/ReactivationRequests", label: "Reactivation Requests" },
      { key: "/TempAccessRequests", label: "Temp Catalogue Access" },
    ],
  },
  {
    key: "/InstaPostSchedule",
    icon: <ScheduleOutlined />,
    label: "Insta Schedule",
  },
];

const ALL_LEAF_KEYS = NAV_ITEMS.flatMap((item) =>
  item.children ? item.children.map((c) => c.key) : [item.key]
);

const getSelectedKey = (path) => {
  if (path.startsWith("/AdminProductEdit/")) return "/AdminAllProducts";
  return ALL_LEAF_KEYS.find((key) => path === key || path.startsWith(key + "/")) || "";
};

const getOpenKey = (selectedKey) => {
  for (const item of NAV_ITEMS) {
    if (item.children?.some((c) => selectedKey.startsWith(c.key))) {
      return item.key;
    }
  }
  return null;
};

const AdminLayout = ({ userName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  const selectedKey = getSelectedKey(location.pathname);
  const openKey = getOpenKey(selectedKey);

  // Single centralized auth check for all admin pages
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/";
      return;
    }
    axios
      .get(
        global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        if (!res.data.IsActive || res.data.IsDeleted || !res.data.IsEmployee) {
          localStorage.removeItem("token");
          localStorage.removeItem("userName");
          window.location.href = "/";
          return;
        }
        setAuthorized(true);
      })
      .catch(() => {
        // Auth check failed (expired/invalid token, 401, etc). Clear the
        // stale token before redirecting — otherwise App.js sees a token
        // still in localStorage and immediately bounces back to
        // /AdminDashBoard, re-triggering this same failing check forever.
        localStorage.removeItem("token");
        localStorage.removeItem("userName");
        window.location.href = "/";
      })
      .finally(() => setAuthChecked(true));
  }, []);

  const handleMenuClick = ({ key }) => {
    navigate(key);
    if (isMobile) setDrawerOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userName");
    window.location.href = "/";
  };

  const userMenuItems = {
    items: [
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Logout",
        danger: true,
        onClick: handleLogout,
      },
    ],
  };

  if (!authChecked) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!authorized) return null;

  const sideMenu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      defaultOpenKeys={openKey ? [openKey] : []}
      items={NAV_ITEMS}
      onClick={handleMenuClick}
      style={{ border: "none" }}
    />
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Desktop fixed sidebar */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={SIDER_WIDTH}
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
            overflow: "auto",
          }}
        >
          <div
            style={{
              height: HEADER_HEIGHT,
              display: "flex",
              alignItems: "center",
              padding: collapsed ? "0 12px" : "0 20px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              flexShrink: 0,
            }}
          >
            {!collapsed && (
              <Text
                strong
                style={{ color: "#fff", fontSize: 14, whiteSpace: "nowrap" }}
              >
                Admin Panel
              </Text>
            )}
          </div>
          {sideMenu}
        </Sider>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          bodyStyle={{ padding: 0, background: "#001529" }}
          headerStyle={{
            background: "#001529",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
          title={
            <Text strong style={{ color: "#fff" }}>
              Admin Panel
            </Text>
          }
          width={SIDER_WIDTH}
        >
          {sideMenu}
        </Drawer>
      )}

      {/* Main content area */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 80 : SIDER_WIDTH,
          transition: "margin-left 0.2s",
        }}
      >
        <Header
          style={{
            background: "#fff",
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f0f0",
            position: "sticky",
            top: 0,
            zIndex: 99,
            height: HEADER_HEIGHT,
            lineHeight: `${HEADER_HEIGHT}px`,
          }}
        >
          <Button
            type="text"
            icon={
              isMobile ? (
                <MenuOutlined />
              ) : collapsed ? (
                <MenuUnfoldOutlined />
              ) : (
                <MenuFoldOutlined />
              )
            }
            onClick={() =>
              isMobile ? setDrawerOpen(true) : setCollapsed((c) => !c)
            }
          />
          <Dropdown menu={userMenuItems} trigger={["click"]}>
            <Button type="text" icon={<UserOutlined />}>
              {!isMobile && (userName || "Admin")}
            </Button>
          </Dropdown>
        </Header>

        <Content style={{ padding: 16, minHeight: `calc(100vh - ${HEADER_HEIGHT}px)` }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
